import { fm } from 'Components/FormatMessage';
import { ppa } from 'neuro-schemas';
import * as React from 'react';

import type { IPPA_Evaluation_key } from 'neuro-schemas/ppa';

const {
  utils: { resultPicker },
} = ppa;

const PPADiagnosticCriteriaHistory = ({ d }: IOwnProps): React.JSX.Element => {
  const sections: Array<
    SafeExtract<keyof IPPA, 'nonfluentVariantPPA' | 'semanticDementia' | 'logopenicProgressiveAphasia'>
  > = ['nonfluentVariantPPA', 'semanticDementia', 'logopenicProgressiveAphasia'];

  const imagingKeyMap = {
    nonfluentVariantPPA: '_criteriaForDiagnosisSupportedByImagingStudiesForNfvPPAFulfilled',
    semanticDementia: '_criteriaForDiagnosisSupportedByImagingStudiesForSemanticDementiaFulfilled',
    logopenicProgressiveAphasia:
      '_criteriaForDiagnosisSupportedByImagingStudiesForLogopenicProgressiveAphasiaFulfilled',
  } satisfies Record<(typeof sections)[number], IPPA_Evaluation_key>;

  const neuropathologicalKeyMap = {
    nonfluentVariantPPA: '_criteriaForNeuropathologicallyConfirmedDiagnosisForNfvPPAFulfilled',
    semanticDementia: '_criteriaForNeuropathologicallyConfirmedDiagnosisForSemanticDementiaFulfilled',
    logopenicProgressiveAphasia:
      '_criteriaForNeuropathologicallyConfirmedDiagnosisForLogopenicProgressiveAphasiaFulfilled',
  } satisfies Record<(typeof sections)[number], IPPA_Evaluation_key>;

  const imagingCriteriaFulfilled = sections.filter(section => {
    const result = resultPicker(d, '_summary', undefined, imagingKeyMap[section]);
    return result === 'yes';
  });

  const neuropathologicalCriteriaFulfilled = sections.filter(section => {
    const result = resultPicker(d, '_summary', undefined, neuropathologicalKeyMap[section]);
    return result === 'yes';
  });

  const generalKeys: Array<keyof NonNullable<IPPA['general']>> = [
    'theFollowingThreeCharacteristicsShouldBePresentInTheSyndrome',
    'theFollowingFourCharacteristicsShouldNotBePresentInTheSyndrome',
  ];

  const generalCriteriaFulfilled =
    imagingCriteriaFulfilled.length > 0 || neuropathologicalCriteriaFulfilled.length > 0
      ? undefined
      : generalKeys.every(k => resultPicker(d, 'general', k) === 'yes')
        ? 'yes'
        : generalKeys.some(k => resultPicker(d, 'general', k) === 'no')
          ? 'no'
          : 'unknown';

  return (
    <React.Fragment>
      {imagingCriteriaFulfilled.length > 0 && (
        <div>
          <div>{fm('diagnosis.ppa.logopenicProgressiveAphasia.summary.imagingCriteriaFulfilled')}:</div>
          <span style={{ fontWeight: 400, whiteSpace: 'pre-line' }}>
            {imagingCriteriaFulfilled.map((r, i, arr) => (
              <span key={r}>
                {fm(`diagnosis.ppa.${r}.titleShort`)}
                {i < arr.length - 1 ? <br /> : null}
              </span>
            ))}
          </span>
        </div>
      )}
      {neuropathologicalCriteriaFulfilled.length > 0 && (
        <div>
          <div>{fm('diagnosis.ppa.logopenicProgressiveAphasia.summary.neuropathologicalCriteriaFulfilled')}:</div>
          <span style={{ fontWeight: 400, whiteSpace: 'pre-line' }}>
            {neuropathologicalCriteriaFulfilled.map((r, i, arr) => (
              <span key={r}>
                {fm(`diagnosis.ppa.${r}.titleShort`)}
                {i < arr.length - 1 ? <br /> : null}
              </span>
            ))}
          </span>
        </div>
      )}
      {generalCriteriaFulfilled &&
        fm(
          `diagnosis.ppa.logopenicProgressiveAphasia.summary.generalCriteria${generalCriteriaFulfilled === 'yes' ? '' : 'Not'}Fulfilled`,
        )}
    </React.Fragment>
  );
};

interface IOwnProps {
  d: IPPA;
}

export default PPADiagnosticCriteriaHistory;
